import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  SparklesIcon,
  AcademicCapIcon,
  UserGroupIcon,
  ChartBarIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* 히어로 섹션 */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex justify-center mb-6"
            >
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 rounded-3xl shadow-2xl">
                <ShieldCheckIcon className="h-20 w-20 text-white" />
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-6xl font-bold mb-6"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
                SafeAI Kids
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-gray-700 mb-4"
            >
              초등학생을 위한 안전한 AI 교육 플랫폼
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-md text-gray-600 mb-8"
            >
              완도교육지원청과 Upstage가 지원하는 교육 서비스
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/login"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
              >
                로그인
              </Link>
              <Link
                to="/register"
                className="px-8 py-4 bg-white text-purple-600 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 border-2 border-purple-600"
              >
                회원가입
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 5단계 필터링 시스템 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              5단계 안전 필터링 시스템
            </span>
          </h2>
          <p className="text-center text-gray-600 mb-12">
            학생들의 안전한 AI 학습 환경을 위한 다층 보안 시스템
          </p>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              {
                icon: <LockClosedIcon className="h-8 w-8" />,
                title: "1단계",
                subtitle: "하드코딩 차단",
                description: "부적절한 단어를 1차적으로 걸러냅니다",
                color: "from-red-500 to-orange-500"
              },
              {
                icon: <SparklesIcon className="h-8 w-8" />,
                title: "2단계",
                subtitle: "AI 프롬프팅",
                description: "학생의 말을 AI가 한 번 더 번역합니다",
                color: "from-orange-500 to-yellow-500"
              },
              {
                icon: <ShieldCheckIcon className="h-8 w-8" />,
                title: "3단계",
                subtitle: "Solar Pro2",
                description: "업스테이지 모델 자체 필터링",
                color: "from-yellow-500 to-green-500"
              },
              {
                icon: <AcademicCapIcon className="h-8 w-8" />,
                title: "4단계",
                subtitle: "출력 조정",
                description: "연령에 맞는 말투와 언어 사용",
                color: "from-green-500 to-blue-500"
              },
              {
                icon: <UserGroupIcon className="h-8 w-8" />,
                title: "5단계",
                subtitle: "교사 모니터링",
                description: "실시간 활동 추적 가능",
                color: "from-blue-500 to-purple-500"
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300"
              >
                <div className={`bg-gradient-to-r ${step.color} w-16 h-16 rounded-2xl flex items-center justify-center text-white mb-4 mx-auto`}>
                  {step.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                  {step.title}
                </h3>
                <p className="text-sm font-semibold text-gray-700 text-center mb-2">
                  {step.subtitle}
                </p>
                <p className="text-xs text-gray-600 text-center">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* 주요 기능 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              주요 기능
            </span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl p-8 text-white shadow-2xl"
            >
              <AcademicCapIcon className="h-12 w-12 mb-4" />
              <h3 className="text-2xl font-bold mb-4">학생 모드</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>안전한 AI 대화 학습</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>학년별 맞춤 콘텐츠</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>AI 이미지 생성 체험</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>실시간 안전 보호</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-3xl p-8 text-white shadow-2xl"
            >
              <ChartBarIcon className="h-12 w-12 mb-4" />
              <h3 className="text-2xl font-bold mb-4">교사 모드</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>실시간 학생 활동 모니터링</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>콘텐츠 필터링 관리</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>학습 자료 업로드</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>학습 분석 리포트</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-4">SafeAI Kids</h3>
              <p className="text-gray-400 text-sm">
                초등학생을 위한 안전한 AI 교육 플랫폼
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">바로가기</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                    로그인
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="text-gray-400 hover:text-white transition-colors">
                    회원가입
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
                    개인정보처리방침
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">문의</h3>
              <p className="text-gray-400 text-sm mb-2">
                <strong>개발자:</strong> 완도고등학교 국어교사 공지훈
              </p>
              <p className="text-gray-400 text-sm mb-2">
                <strong>이메일:</strong> purusil55@gmail.com
              </p>
              <p className="text-gray-400 text-sm">
                <strong>지원기관:</strong> 완도교육지원청/Upstage
              </p>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 SafeAI Kids. All rights reserved.</p>
            <p className="mt-2">완도교육지원청과 Upstage가 지원하는 안전한 AI 교육 플랫폼</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
